import {FrontWindow, globalDocument} from '../libs/shared/helpers/src/browser/browserHelpers';
import {parseQueryString} from '../libs/shared/helpers/src/browser/urlHelpers';
import {isElectron} from '../libs/shared/helpers/src/environment/platformHelpers';
import {logger} from '../srcCommon/services/logger/logger';
import {withTimeoutAsync} from './helpers/asyncHelpers';
import {ensureLoadedOnceInBrowsingContext} from './services/loadedOnce';

/*
 * Pre-flight.
 */

export function ensureSafeToLoadInWindow(window: FrontWindow) {
  // https://paper.dropbox.com/doc/2021-12-22-Web-client-loading-infinitely-nested-bDKrEMpS4BZYR6JZJoGMd
  ensureLoadedOnceInBrowsingContext(window);

  // Out of an abundance of caution; should be caught by ensureLoadedOnceInBrowsingContext().
  if (window.location.pathname === '/cards') {
    throw new Error('Refusing to serve Old Front /cards route, this is New Front!');
  }

  // https://github.com/frontapp/front-client/pull/4961
  const query = parseQueryString(window.location.search);
  if (query.serve === 'legacy') {
    throw new Error('Failed to serve Old Front, this is New Front!');
  }
}

/*
 * Loading overlay.
 */

const loadingOverlayLogger = logger.getLogger('loadingOverlay');
const hideLoadingOverlayTimeout = 3100;

export function hideLoadingOverlay() {
  const loadingElement = globalDocument.getElementById('front-client-loading');
  if (!loadingElement) {
    loadingOverlayLogger.error('Failed to find loading overlay element');
    return;
  }

  withTimeoutAsync(
    () => hideLoadingOverlayAsync(loadingElement),
    hideLoadingOverlayTimeout,
    () => {
      throw new Error('Timed out while hiding loading overlay');
    },
  ).catch((error) => {
    loadingElement.remove();
    loadingOverlayLogger.error(error.message);
  });
}

async function hideLoadingOverlayAsync(loadingElement: Element) {
  // As of 08/10/2020: the Electron app has its own loading mechanism so waiting would cause a flicker.
  // TODO: remove this check when the Electron app gets rid of its own loading logo.
  if (!isElectron()) {
    await fadeOutLoadingOverlayAsync(loadingElement);
  }

  loadingElement.remove();
}

async function fadeOutLoadingOverlayAsync(loadingElement: Element) {
  const transitionEndPromise = maybeWaitForTransitionEndAsync(loadingElement);
  loadingElement.classList.remove('show');
  await transitionEndPromise;
}

async function maybeWaitForTransitionEndAsync(element: Element): Promise<void> {
  const hasTransitionEndEvent = 'ontransitionend' in element;
  if (!hasTransitionEndEvent) {
    return;
  }

  await new Promise((resolve) => {
    element.addEventListener('transitionend', resolve, {once: true});
  });
}
